// RegisterNowComponent.jsx (payment fully removed, integrated with InviteeService step1 & step2)
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./RegisterNowComponent.css";
import cardImg from "../../assets/cardimg.png";
import { FaChevronDown, FaArrowLeft } from "react-icons/fa";
import InviteeService from "../../core/service/InviteeService";
import EventService from "../../core/service/EventService";
import {
  formatEventDisplayDate,
  getEventSortDate,
  isEventUpcoming,
} from "../../utils/eventDateUtils";

const RegisterNow = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize services with useMemo to prevent unnecessary re-creation
  const inviteeService = useMemo(() => new InviteeService(), []);
  const eventService = useMemo(() => new EventService(), []);

  // Define options for dropdowns
  const industryOptions = [
    "TMTs",
    "BFSI",
    "Manufacturing",
    "Pharma",
    "FMCG",
    "Consulting",
    "Real",
  ];

  const areaOfInterestOptions = [
    "CRE",
    "Facilities",
    "Workplace Experience",
    "Sustainability",
  ];

  const [formData, setFormData] = useState({
    passType: "invitee",
    companyName: "",
    name: "",
    email: "",
    phone: "",
    function: "",
    role: "",
    designation: "",
    linkedin: "",
    city: "",
    state: "",
    industry: "",
    areaOfInterest: [],
    profilePhoto: null,
    idProof: null,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isAreaOfInterestOpen, setIsAreaOfInterestOpen] = useState(false);
  const [eventDetails, setEventDetails] = useState(null);
  const [isFetchingEvent, setIsFetchingEvent] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // Step 1: Basic Info, Step 2: Company Details
  const [showThankYouModal, setShowThankYouModal] = useState(false);

  // New state: inviteeId returned by Step-1 for new invitees
  const [inviteeId, setInviteeId] = useState(null);

  // Use refs to track if we've already processed certain effects
  const hasProcessedEventDetails = useRef(false);

  // Function to fetch event details by EUID - wrapped in useCallback
  const fetchEventDetails = useCallback(
    async (euid) => {
      try {
        setIsFetchingEvent(true);
        const response = await eventService.getEventByEuid(euid);

        if (response.data && response.data.responseBody) {
          const event = response.data.responseBody;

          // Get city image
          const getCityImage = (city) => {
            switch (city.toLowerCase()) {
              case "pune":
                return require("../../assets/puneImg.png");
              case "bangalore":
                return require("../../assets/BangImg.png");
              case "hyderabad":
                return require("../../assets/HydImg.png");
              case "chennai":
                return require("../../assets/CheImg.png");
              default:
                return require("../../assets/BangImg.png");
            }
          };

          // Format date
          const newEventDetails = {
            city: event.city,
            date: formatEventDisplayDate(event),
            image: getCityImage(event.city),
            euid: event.euid || "",
            eventName: event.eventName || "",
            venue: event.venue || "",
            eventTime: event.eventTime || "",
            fullEvent: event,
          };

          setEventDetails(newEventDetails);
          hasProcessedEventDetails.current = true;
          return newEventDetails;
        } else {
          toast.error("Event not found. Please try again or contact support.");
          return null;
        }
      } catch (error) {
        toast.error("Failed to load event details");
        console.error("Error fetching event details:", error);
        return null;
      } finally {
        setIsFetchingEvent(false);
      }
    },
    [eventService]
  );

  // Read event param from URL (optional)
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const eventParam = queryParams.get("event");

    if (eventParam && !hasProcessedEventDetails.current) {
      fetchEventDetails(eventParam);
    }
  }, [location.search, fetchEventDetails]);

  // Get event details from location state or fetch the latest event
  useEffect(() => {
    if (hasProcessedEventDetails.current) return;

    if (location.state) {
      setEventDetails(location.state);
      hasProcessedEventDetails.current = true;
      return;
    }

    if (!eventDetails && !hasProcessedEventDetails.current) {
      const fetchLatestEvent = async () => {
        try {
          setIsFetchingEvent(true);
          const response = await eventService.getAllEvents();

          if (response.data && response.data.responseBody) {
            const sortedEvents = [...response.data.responseBody].sort(
              (a, b) => getEventSortDate(a) - getEventSortDate(b)
            );

            const upcomingEvents = sortedEvents.filter((event) =>
              isEventUpcoming(event)
            );

            if (upcomingEvents.length > 0) {
              const latestEvent = upcomingEvents[0];

              const getCityImage = (city) => {
                switch (city.toLowerCase()) {
                  case "pune":
                    return require("../../assets/puneImg.png");
                  case "bangalore":
                    return require("../../assets/BangImg.png");
                  case "hyderabad":
                    return require("../../assets/HydImg.png");
                  case "chennai":
                    return require("../../assets/CheImg.png");
                  default:
                    return require("../../assets/BangImg.png");
                }
              };

              setEventDetails({
                city: latestEvent.city,
                date: formatEventDisplayDate(latestEvent),
                image: getCityImage(latestEvent.city),
                euid: latestEvent.euid || "",
                eventName: latestEvent.eventName || "",
                venue: latestEvent.venue || "",
                eventTime: latestEvent.eventTime || "",
                fullEvent: latestEvent,
              });
              hasProcessedEventDetails.current = true;
            } else {
              toast.error("No upcoming events found");
            }
          }
        } catch (error) {
          toast.error("Failed to load event details");
        } finally {
          setIsFetchingEvent(false);
        }
      };

      fetchLatestEvent();
    }
  }, [location.state, eventService, eventDetails]);


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isAreaOfInterestOpen &&
        !event.target.closest(".custom-multi-select")
      ) {
        setIsAreaOfInterestOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isAreaOfInterestOpen]);

  // Prevent body scroll when thank-you modal is open
  useEffect(() => {
    if (showThankYouModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showThankYouModal]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files && files[0]) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          [name]: reader.result,
        }));
      };
      reader.readAsDataURL(files[0]);
    } else {
      if (name === "linkedin") {
        setFormData((prev) => ({
          ...prev,
          linkedin: value,
          linkedInProfile: value,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    }
  };

  const handleAreaOfInterestChange = (option) => {
    const currentSelection = [...formData.areaOfInterest];
    const index = currentSelection.indexOf(option);

    if (index === -1) {
      currentSelection.push(option);
    } else {
      currentSelection.splice(index, 1);
    }

    setFormData((prev) => ({
      ...prev,
      areaOfInterest: currentSelection,
    }));
  };

  const validateStep1 = () => {
    if (!formData.name || !formData.name.trim()) {
      toast.error("Name is required");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return false;
    }

    const phoneDigits = formData.phone.replace(/\D/g, "");
    if (phoneDigits.length !== 10) {
      toast.error("Please enter a valid 10-digit phone number");
      return false;
    }

    return true;
  };

  const validateStep2 = () => {
    const requiredFields = [formData.companyName, formData.designation];

    const fieldNames = ["Company Name", "Designation"];

    for (let i = 0; i < requiredFields.length; i++) {
      if (!requiredFields[i] || requiredFields[i].toString().trim() === "") {
        toast.error(`${fieldNames[i]} is required`);
        return false;
      }
    }

    return true;
  };

  const handleStep1Submit = async (e) => {
  e.preventDefault();

  if (!validateStep1()) return;

  try {
    setIsLoading(true);

    const dto = {
      name: formData.name,
      email: formData.email,
      mobileNumber: formData.phone,
    };

    const response = await inviteeService.step1(
      eventDetails.euid,
      dto
    );

    const {
      responseCode,
      responseMessage,
      responseBody,
    } = response.data;

    // ✅ EXISTING INVITEE
    if (responseCode === 200 && responseMessage === "INVITEE_REGISTERED") {
      setShowThankYouModal(true);
      return;
    }

    // ✅ NEW INVITEE
    if (responseCode === 200 && responseMessage === "NEW_INVITEE") {
      if (responseBody?.inviteeId) {
        setInviteeId(responseBody.inviteeId);
        setCurrentStep(2);
      } else {
        toast.error("Invitee ID not returned");
      }
      return;
    }

    // ✅ ALREADY REGISTERED (THIS WAS FAILING)
    if (responseCode === 400 && responseMessage === "ALREADY_REGISTERED") {
      toast.error(responseBody || "User already registered");

      // ✅ CLEAR INPUT FIELDS
      setFormData({
        name: "",
        email: "",
        phone: "",
      });
      return;
    }

    toast.error(responseMessage || "Registration failed");

  } catch (err) {
    console.error(err);
    toast.error("Something went wrong");
  } finally {
    setIsLoading(false);
  }
};



  const handleGoBack = () => {
    setCurrentStep(1);
  };

  // Final submit: calls submitStep2 (no payment)
  const handleFinalSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep2()) {
      return;
    }

    if (!inviteeId) {
      toast.error("Invitee ID is missing. Please complete Step 1 first.");
      return;
    }

    try {
      setIsLoading(true);

      const profile = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        companyName: formData.companyName,
        function: formData.function,
        role: formData.role,
        designation: formData.designation,
        linkedInProfile: formData.linkedin,
        city: formData.city,
        state: formData.state,
        industry: formData.industry,
        areaOfInterest: formData.areaOfInterest,
        profilePhoto: formData.profilePhoto,
        idProof: formData.idProof,
      };

      // payment is false for non-paid flow
      const payment = { isPaymentSuccessful: false };

      const response = await inviteeService.submitStep2(
        eventDetails.euid, // ✅ euid
        inviteeId, // ✅ path variable
        profile,
        payment
      );

      const httpStatus = response.status;
      const responseMessage = response.data?.responseMessage;

      if (
        (httpStatus === 200 && responseMessage === "INVITEE_REGISTERED") ||
        httpStatus === 201 ||
        responseMessage === "REGISTRATION_COMPLETE"
      ) {
        setShowThankYouModal(true);
      } else if (httpStatus === 409 && responseMessage === "PAYMENT_REQUIRED") {
        setShowThankYouModal(true);
      } else {
        toast.error(responseMessage || "Failed to complete registration");
      }
    } catch (error) {
      console.error("Error in Step-2 submission:", error);
      let errorMsg = "Failed to complete registration";
      if (error.response?.data?.responseMessage) {
        errorMsg = error.response.data.responseMessage;
      }
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleThankYouOkay = () => {
    setShowThankYouModal(false);
    navigate("/");
  };

  if (isFetchingEvent) {
    return (
      <div className="register-page">
        <div className="register-container">
          <div className="loading-message">
            <div className="loading-spinner"></div>
            <p>Loading event details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!eventDetails) {
    return (
      <div className="register-page">
        <div className="register-container">
          <div className="error-message">
            <p>
              No event details available. Please access this page from the
              platform page.
            </p>
            <button
              className="form-submit"
              onClick={() => navigate("/platform-page")}
            >
              Go to Platform Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="register-page">
        <div className="register-container">
          <div className="loading-message">
            <div className="loading-spinner"></div>
            <p>Initializing event...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="register-page">
        {isLoading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <p>Processing registration...</p>
          </div>
        )}

        <div className="register-container">
          <div className="header-section">
            <h1 className="register-title">Register Now</h1>
            <div className="event-card">
              <div className="event-image">
                <img
                  src={eventDetails?.image}
                  alt={`${eventDetails?.city} Event`}
                  className="event-image-img"
                  onError={(e) => {
                    e.target.src = cardImg;
                  }}
                />
              </div>
              <div className="event-info">
                <h2 className="event-city">
                  {eventDetails?.city} | {eventDetails?.date}
                </h2>
              </div>
            </div>
          </div>

          {/* Only show forms if thank you modal is not showing */}
          {!showThankYouModal && (
            <>
              {/* STEP 1: Basic Information */}
              {currentStep === 1 && (
                <form className="register-form" onSubmit={handleStep1Submit}>
                  <div className="form-section-heading">
                    <div className="heading-with-radio">
                      <div className="radio-indicator">
                        <div className="radio-circle filled"></div>
                      </div>
                      <h2 className="registration-type-heading">
                        Invitee Registration
                      </h2>
                    </div>
                  </div>

                  <div className="form-group">
                    <input
                      type="text"
                      name="name"
                      placeholder="Name *"
                      className="form-input"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      disabled={isLoading}
                    />
                  </div>

                  <div className="form-group">
                    <input
                      type="email"
                      name="email"
                      placeholder="Email *"
                      className="form-input"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={isLoading}
                    />
                  </div>

                  <div className="form-group">
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number *"
                      className="form-input"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      disabled={isLoading}
                    />
                  </div>

                  <button
                    type="submit"
                    className="form-submit"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <span>
                        Processing...
                        <span className="button-spinner"></span>
                      </span>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </form>
              )}

              {/* STEP 2: Company Details */}
              {currentStep === 2 && (
                <form className="register-form" onSubmit={handleFinalSubmit}>
                  <div className="form-section-heading">
                    <button
                      type="button"
                      className="go-back-button"
                      onClick={handleGoBack}
                    >
                      <FaArrowLeft /> Go Back
                    </button>
                    <p className="verification-text">
                      Please fill the details for us to verify your profile
                    </p>
                  </div>

                  <div className="form-group">
                    <input
                      type="text"
                      name="companyName"
                      placeholder="Company name *"
                      className="form-input"
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                      disabled={isLoading}
                    />
                  </div>

                  <div className="form-group">
                    <input
                      type="text"
                      name="designation"
                      placeholder="Designation *"
                      className="form-input"
                      value={formData.designation}
                      onChange={handleChange}
                      required
                      disabled={isLoading}
                    />
                  </div>

                  <div className="form-group">
                    <select
                      name="industry"
                      className="form-select"
                      value={formData.industry}
                      onChange={handleChange}
                      disabled={isLoading}
                    >
                      <option value="">Industry</option>
                      {industryOptions.map((industry, index) => (
                        <option key={index} value={industry}>
                          {industry}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <div className="custom-multi-select">
                      <div
                        className={`custom-multi-select-trigger ${
                          isLoading ? "disabled" : ""
                        }`}
                        onClick={() =>
                          !isLoading &&
                          setIsAreaOfInterestOpen(!isAreaOfInterestOpen)
                        }
                      >
                        <div className="selected-values">
                          {formData.areaOfInterest.length > 0
                            ? formData.areaOfInterest.join(", ")
                            : "Area of interest"}
                        </div>
                        <FaChevronDown
                          className={`dropdown-icon ${
                            isAreaOfInterestOpen ? "open" : ""
                          }`}
                        />
                      </div>

                      {isAreaOfInterestOpen && (
                        <div className="custom-multi-select-dropdown">
                          {areaOfInterestOptions.map((option, index) => (
                            <div
                              key={index}
                              className="custom-multi-select-option"
                            >
                              <input
                                type="checkbox"
                                id={`area-${index}`}
                                checked={formData.areaOfInterest.includes(
                                  option
                                )}
                                onChange={() =>
                                  handleAreaOfInterestChange(option)
                                }
                                disabled={isLoading}
                              />
                              <label htmlFor={`area-${index}`}>{option}</label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="form-submit"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <span>
                        Submitting...
                        <span className="button-spinner"></span>
                      </span>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>

      {/* Thank You Modal */}
      {showThankYouModal && (
        <div className="payment-modal-overlay show">
          <div className="thank-you-modal">
            <div className="modal-content">
              <h3 className="thank-you-title">Thank You</h3>
              <p className="thank-you-text">
                Thank you for your interest, we will verify your profile
              </p>
              <div className="card-container">
                <button
                  className="modal-pay-button"
                  onClick={handleThankYouOkay}
                >
                  Okay
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {!showThankYouModal && (
        <ToastContainer
          position="bottom-left"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnHover
          draggable
          style={{
            fontSize: "14px",
          }}
          toastStyle={{
            background: "#fff",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          }}
        />
      )}
    </>
  );
};

export default RegisterNow;
